// FundamentalTuneCell renders the compare/preview cell for the fundamental.
// It bridges the scale settings UI to the transient tuning-preview runtime so
// users can audition fundamental changes before committing them.

import { useEffect, useRef, useCallback } from "preact/hooks";
import PropTypes from "prop-types";
import {
  getFundamentalDeviationCents,
  isFundamentalComparing,
} from "../../tuning/tuning-preview-runtime.js";

/**
 * Inline drag-to-tune handle for the Reference Frequency.
 * Drag shifts all sounding notes by the delta in cents.
 * On save: newFundamental = fundamental * 2^(delta/1200). Scale unchanged.
 */
const FundamentalTuneCell = ({
  fundamental,
  previewState,
  keysRef,
  onChange,
  onPreviewChange,
  resetToken = 0,
}) => {
  const dragStart = useRef(null);
  const dragKeysInstance = useRef(null);
  const previousFundamental = useRef(fundamental);
  const previousResetToken = useRef(resetToken);
  const previousDirty = useRef(false);

  useEffect(() => {
    const currentKeys = keysRef?.current ?? null;
    return () => {
      if (currentKeys?.previewFundamental) currentKeys.previewFundamental(0, true);
      if (dragKeysInstance.current?.setTuneDragging)
        dragKeysInstance.current.setTuneDragging(false);
    };
  }, [keysRef]);

  const deltaCents = getFundamentalDeviationCents(previewState);
  const comparing = isFundamentalComparing(previewState);
  const isDirty = deltaCents !== null && Math.abs(deltaCents) > 0.001;
  const delta = isDirty ? deltaCents : 0;
  const deltaStr = delta >= 0 ? `+${delta.toFixed(1)}¢` : `${delta.toFixed(1)}¢`;

  useEffect(() => {
    const fundamentalChanged = previousFundamental.current !== fundamental;
    const resetTokenChanged = previousResetToken.current !== resetToken;
    previousFundamental.current = fundamental;
    previousResetToken.current = resetToken;
    const previewClearedExternally = previousDirty.current && !isDirty;
    previousDirty.current = isDirty;
    if (!fundamentalChanged && !resetTokenChanged && !previewClearedExternally) {
      return;
    }
    if (keysRef?.current?.previewFundamental) keysRef.current.previewFundamental(0, true);
  }, [fundamental, resetToken, isDirty, keysRef]);

  const onCompare = useCallback(() => {
    const next = !comparing;
    onPreviewChange?.(deltaCents, next);
    if (keysRef?.current?.previewFundamental)
      keysRef.current.previewFundamental(next ? 0 : deltaCents);
  }, [comparing, deltaCents, keysRef, onPreviewChange]);

  const onPointerDown = useCallback(
    (e) => {
      if (keysRef?.current?.setTuneDragging) {
        keysRef.current.setTuneDragging(true);
        dragKeysInstance.current = keysRef.current;
      }
      // Snapshot base pitch of all sounding notes before drag begins
      if (keysRef?.current?.snapshotForFundamentalPreview)
        keysRef.current.snapshotForFundamentalPreview();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragStart.current = { lastX: e.clientX, acc: 0 };
    },
    [keysRef],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragStart.current) return;
      const dx = e.clientX - dragStart.current.lastX;
      if (dx === 0) return;
      const speed = Math.abs(dx);
      const sensitivity = 0.05 * Math.pow(speed, 1.5);
      const newDelta = dragStart.current.acc + Math.sign(dx) * sensitivity;
      dragStart.current.lastX = e.clientX;
      dragStart.current.acc = newDelta;
      onPreviewChange?.(newDelta, false);
      if (keysRef?.current?.previewFundamental) keysRef.current.previewFundamental(newDelta);
    },
    [keysRef, onPreviewChange],
  );

  const onPointerUp = useCallback(() => {
    dragStart.current = null;
    if (dragKeysInstance.current?.setTuneDragging) {
      dragKeysInstance.current.setTuneDragging(false);
      if (dragKeysInstance.current.state?.escHeld) dragKeysInstance.current.sustainOn();
    }
  }, []);

  const onSave = useCallback(() => {
    const newFundamental = fundamental * Math.pow(2, deltaCents / 1200);
    onChange("fundamental", newFundamental);
    // Restore notes to their scale-derived pitch — Keys rebuild will use new fundamental.
    // clearSnapshot=true ends the drag session; updateFundamental in keys.js also clears it.
    onPreviewChange?.(null, false);
  }, [deltaCents, fundamental, onChange, onPreviewChange]);

  const onRevert = useCallback(() => {
    // clearSnapshot=true ends the drag session so the snapshot is not reused.
    if (keysRef?.current?.previewFundamental) keysRef.current.previewFundamental(0, true);
    onPreviewChange?.(null, false);
  }, [keysRef, onPreviewChange]);

  return (
    <div class="tune-cell--inline">
      {isDirty && (
        <span class={`tune-delta${comparing ? " tune-comparing" : ""}`}>
          {comparing ? "orig" : deltaStr}
        </span>
      )}
      {isDirty && (
        <button
          type="button"
          class={`tune-btn${comparing ? " tune-btn--active" : ""}`}
          onClick={onCompare}
          title="A/B compare with original"
        >
          <span class="refresh-glyph tune-btn-compare" aria-hidden="true">
            ⟳
          </span>
        </button>
      )}
      {isDirty && (
        <button
          type="button"
          class="tune-btn tune-btn--save"
          onClick={onSave}
          title="Save new Reference Frequency"
        >
          ✓
        </button>
      )}
      {isDirty && (
        <button type="button" class="tune-btn tune-btn--revert" onClick={onRevert} title="Revert">
          ✕
        </button>
      )}
      <span
        class="tune-handle tune-handle--padded"
        title="Drag to adjust Reference Frequency — slow for fine, fast for coarse"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        ⟺
      </span>
    </div>
  );
};

FundamentalTuneCell.propTypes = {
  fundamental: PropTypes.number.isRequired,
  previewState: PropTypes.shape({
    fundamentalDeltaCents: PropTypes.number,
    fundamentalComparing: PropTypes.bool,
    degreePreviews: PropTypes.object,
  }),
  keysRef: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onPreviewChange: PropTypes.func,
  resetToken: PropTypes.number,
};

export default FundamentalTuneCell;
